import React, {
  forwardRef,
  useImperativeHandle,
  useState,
  ForwardRefRenderFunction,
  ReactElement,
} from 'react';
import { Checkbox, Input } from 'antd';
import '../ReportingAndAnalytics.less';
import { ReactComponent as DownOutlined } from '../../../assets/images/svg/ReportingDownChevronUnselected.svg';
import { ReactComponent as UpOutlined } from '../../../assets/images/svg/ReportingUpChevronSelected.svg';
import { isEmpty } from 'lodash';
import { REPORTING_CONSTANTS } from './reportingAndAnalyticsConstants';
import {
  GenericExpandableFilterProps,
  GenericExpandableFilterRef,
} from '../../../shared/interfaces/reportingAndAnalyticsInterface';

const GenericExpandableFilter: ForwardRefRenderFunction<
  GenericExpandableFilterRef,
  GenericExpandableFilterProps
> = (
  { label, options, onChange, showMoreLimit = 5, enableSearch = false }: any,
  ref: any,
): ReactElement => {
  const [selectedOptions, setSelectedOptions] = useState<string[]>([
    REPORTING_CONSTANTS.ALL,
  ]);
  const [expanded, setExpanded] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [searchText, setSearchText] = useState('');

  const isAllSelected = selectedOptions?.includes(REPORTING_CONSTANTS.ALL);
  const realSelected = isAllSelected ? [] : selectedOptions;
  const resetFilter = (): void => {
    setSelectedOptions([]);
    setExpanded(false);
    setShowAll(false);
    setSearchText('');
    onChange([]);
  };

  useImperativeHandle(ref, (): any => ({
    // to call reset the filter from parent component
    resetFilter,
  }));

  const handleCheckboxChange = (value: string): void => {
    let updated: string[];

    if (value === REPORTING_CONSTANTS.ALL) {
      updated = [REPORTING_CONSTANTS.ALL];
    } else {
      if (selectedOptions?.includes(value)) {
        updated = selectedOptions?.filter(
          (selectedValue: string): boolean => selectedValue !== value,
        );
      } else {
        updated = [
          ...selectedOptions?.filter(
            (selectedValue: string): boolean =>
              selectedValue !== REPORTING_CONSTANTS.ALL,
          ),
          value,
        ];
      }
    }

    setSelectedOptions(updated);
    onChange(value === REPORTING_CONSTANTS.ALL ? [] : updated);
  };

  const filteredOptions = options?.filter((option: any): boolean =>
    option?.label?.toLowerCase()?.includes(searchText?.toLowerCase()),
  );

  const selectedFiltered = filteredOptions?.filter(
    (option: any): boolean =>
      !isAllSelected && realSelected?.includes(option?.value),
  );
  const unselectedFiltered = filteredOptions?.filter(
    (option: any): boolean =>
      isAllSelected || !realSelected?.includes(option?.value),
  );
  const orderedOptions = [...selectedFiltered, ...unselectedFiltered];

  const visibleOptions = showAll
    ? orderedOptions
    : orderedOptions?.slice(0, showMoreLimit);

  const allSelected =
    !isEmpty(selectedOptions) &&
    !selectedOptions?.includes(REPORTING_CONSTANTS.ALL);
  return (
    <div className="expandable-filter-container">
      <div
        className="expandable-filter-header"
        onClick={(): void => setExpanded((prev: boolean): boolean => !prev)}
      >
        <Checkbox
          checked={allSelected}
          onClick={(e: React.MouseEvent<HTMLDivElement>): void =>
            e?.stopPropagation()
          }
          className={`${
            allSelected
              ? 'custom-checkbox-selected'
              : 'custom-checkbox-unselected'
          } customCheckBox`}
        />
        <span className={`filter-label ${allSelected && 'selected-label'}`}>
          {label}
        </span>
        {expanded ? <UpOutlined /> : <DownOutlined />}
      </div>

      {expanded && (
        <>
          {enableSearch && (
            <Input
              className="customSearchInput"
              placeholder="Search Payer Name"
              value={searchText}
              onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                setSearchText(e?.target?.value);
                setShowAll(false);
              }}
              allowClear={true}
              size="small"
            />
          )}
          <div className="expandable-filter-content">
            {!searchText && (
              <div className="checkbox-item">
                <Checkbox
                  checked={isAllSelected || isEmpty(selectedOptions)}
                  onChange={(): void =>
                    handleCheckboxChange(REPORTING_CONSTANTS.ALL)
                  }
                  className={`${
                    isAllSelected
                      ? 'custom-checkbox-selected'
                      : 'custom-checkbox-unselected'
                  } customCheckBox`}
                >
                  <span className="ellipsis-label" title="All">
                    All
                  </span>
                </Checkbox>
              </div>
            )}

            {visibleOptions?.map(
              (option: any): ReactElement => (
                <div key={option?.value} className="checkbox-item">
                  <Checkbox
                    checked={
                      isAllSelected
                        ? false
                        : selectedOptions?.includes(option?.value)
                    }
                    onChange={(): void => handleCheckboxChange(option?.value)}
                    className={`${
                      selectedOptions?.includes(option?.value)
                        ? 'custom-checkbox-selected'
                        : 'custom-checkbox-unselected'
                    } customCheckBox`}
                  >
                    <span className="ellipsis-label" title={option?.label}>
                      {option?.label}
                    </span>
                  </Checkbox>
                </div>
              ),
            )}

            {orderedOptions?.length > showMoreLimit && (
              <div
                className="show-more-toggle mouse-pointer"
                onClick={(): void =>
                  setShowAll((prev: boolean): boolean => !prev)
                }
              >
                {showAll ? 'Show Less' : 'View More'}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default forwardRef(GenericExpandableFilter);
